import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ContractNature,
  ListBuyerEventsApiArg,
  SortDirection,
  SourcingEventSortField,
  useListBuyerEventFilterOptionsQuery,
  useListBuyerEventsQuery,
} from "../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../common/auth/useLoggedInWithOrgContextState";
import { CopiedFromLine } from "./sourcing-events/CopiedFromLine";
import { UnansweredQuestionsBadge } from "../common/components/communication/UnansweredQuestionsBadge";
import {
  EntityTable,
  EntityTableColumn,
} from "../common/entity-table/EntityTable";
import { useColumnMemory } from "../common/entity-table/useColumnMemory";
import { useDisplayDate } from "../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../common/formatting/useDisplayDateWithTime";
import { SelectorValue } from "../common/input/Selector/SelectorValue";
import {
  BUCKET_META,
  STATUSES_BY_BUCKET,
  isBucketKey,
  type BucketKey,
} from "../common/layout/statusBuckets";
import { useNavLabel } from "../common/layout/useNavLabel";
import { useRunsPublicProcurements } from "../common/organization/useRunsPublicProcurements";
import { useProcurementTerms } from "../common/terminology/useProcurementTerms";
import { displayPersonName } from "./contracts/view-single/sharing/AddExternalParticipantModal";
import { getContractNatureLabel } from "./sourcing-events/portal/utils/displayFormatters";
import { useEventNavigation } from "./sourcing-events/useEventNavigation";
import { EventListItemStatusTag } from "./sourcing-event/EventListItemStatusTag";

const PAGE_SIZE = 10;
const CONTRACT_NATURES: ContractNature[] = [
  ContractNature.Works,
  ContractNature.Supplies,
  ContractNature.Services,
  ContractNature.Combined,
];
const BASE_COLUMNS = [
  "title",
  "status",
  "responsiblePersons",
  "contractType",
  "deadline",
  "modifiedAt",
];

export const SourcingEventsList = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const navLabel = useNavLabel();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const authState = useLoggedInWithOrgContextState();
  const navigateToEvent = useEventNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resetKey, setResetKey] = useState(0);
  const [personSearchTerm, setPersonSearchTerm] = useState("");

  // URL is the single source of truth for search/filter/sort/paging state — a filtered view
  // survives refresh and stays in sync with the sidebar's ?status= bucket link.
  const name = searchParams.get("name") ?? "";
  const deadlinePassed = searchParams.get("deadline") === "passed";
  const statusBuckets = (searchParams.get("status") ?? "")
    .split(",")
    .filter(isBucketKey) as BucketKey[];
  const responsiblePersons = (searchParams.get("responsible") ?? "")
    .split(",")
    .filter(Boolean);
  const contractTypes = (searchParams.get("contractType") ?? "")
    .split(",")
    .filter(Boolean) as ContractNature[];
  const sortBy =
    (searchParams.get("sortBy") as SourcingEventSortField | null) ?? undefined;
  const sortDirection =
    (searchParams.get("sortDirection") as SortDirection | null) ?? undefined;
  const skip = Number(searchParams.get("skip") ?? 0);

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilterParam = useCallback(
    (key: string, value: string) => {
      updateParams((params) => {
        if (value) params.set(key, value);
        else params.delete(key);
        params.set("skip", "0");
      });
    },
    [updateParams],
  );

  const debouncedSetName = useMemo(
    () => debounce((value: string) => setFilterParam("name", value), 300),
    [setFilterParam],
  );
  const debouncedPersonSearch = useMemo(
    () => debounce((value: string) => setPersonSearchTerm(value), 300),
    [],
  );

  const handleSort = useCallback(
    (column: SourcingEventSortField) => (direction: SortDirection) => {
      updateParams((params) => {
        if (direction) {
          params.set("sortBy", column);
          params.set("sortDirection", direction);
        } else {
          params.delete("sortBy");
          params.delete("sortDirection");
        }
        params.set("skip", "0");
      });
    },
    [updateParams],
  );

  const columnMemory = useColumnMemory("v2-procurements");

  const handleReset = useCallback(() => {
    columnMemory.forget();
    setSearchParams(new URLSearchParams(), { replace: true });
    setPersonSearchTerm("");
    setResetKey((key) => key + 1);
  }, [columnMemory, setSearchParams]);

  const defaultColumns = useMemo(
    () =>
      runsPublicProcurements
        ? BASE_COLUMNS
        : ["title", "internalReference", ...BASE_COLUMNS.slice(1)],
    [runsPublicProcurements],
  );

  const visibleColumns = useMemo(() => {
    const value = searchParams.get("columns");
    if (!value || !value.trim())
      return new Set<string>(columnMemory.recall() ?? defaultColumns);
    return new Set(
      value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }, [searchParams, defaultColumns, columnMemory]);

  const setVisibleColumns = useCallback(
    (list: string[]) => {
      columnMemory.remember(list);
      updateParams((params) => params.set("columns", list.join(",")));
    },
    [columnMemory, updateParams],
  );
  const onShowColumn = useCallback(
    (key: string) => {
      if (!visibleColumns.has(key)) setVisibleColumns([...visibleColumns, key]);
    },
    [visibleColumns, setVisibleColumns],
  );
  const onHideColumn = useCallback(
    (key: string) =>
      setVisibleColumns([...visibleColumns].filter((k) => k !== key)),
    [visibleColumns, setVisibleColumns],
  );
  const hasColumnChanges = useMemo(
    () =>
      visibleColumns.size !== defaultColumns.length ||
      defaultColumns.some((k) => !visibleColumns.has(k)),
    [visibleColumns, defaultColumns],
  );

  // Context-aware filter options: only responsible persons, contract types and statuses that actually
  // exist in the org's events, so no filter offers a value that would return nothing.
  const { data: filterOptions } = useListBuyerEventFilterOptionsQuery({
    orgId: authState.selectedOrg.id,
  });
  const responsiblePersonOptions = filterOptions?.responsiblePersons ?? [];
  const filteredResponsibleOptions = personSearchTerm
    ? responsiblePersonOptions.filter((p) =>
        displayPersonName(p)
          .toLowerCase()
          .includes(personSearchTerm.toLowerCase()),
      )
    : responsiblePersonOptions;

  // Selected chips are driven by the URL (source of truth); labels resolved from the complete set.
  const selectedResponsibleValues: SelectorValue[] = responsiblePersons.map(
    (id) => {
      const person = responsiblePersonOptions.find((p) => p.id === id);
      return { value: id, label: person ? displayPersonName(person) : id };
    },
  );

  const presentContractNatures = new Set(filterOptions?.contractNatures ?? []);
  const presentStatuses = new Set<string>(filterOptions?.statuses ?? []);
  const presentBuckets = filterOptions
    ? (Object.keys(BUCKET_META) as BucketKey[]).filter((bucket) =>
        STATUSES_BY_BUCKET[bucket].some((s) => presentStatuses.has(s))
      )
    : (Object.keys(BUCKET_META) as BucketKey[]);

  const selectedStatuses = statusBuckets.flatMap((bucket) => STATUSES_BY_BUCKET[bucket]);

  const queryArg: ListBuyerEventsApiArg = {
    orgId: authState.selectedOrg.id,
    name: name || undefined,
    statuses: selectedStatuses.length ? selectedStatuses : undefined,
    responsiblePersons: responsiblePersons.length
      ? responsiblePersons
      : undefined,
    contractTypes: contractTypes.length ? contractTypes : undefined,
    deadlinePassed: deadlinePassed || undefined,
    limit: PAGE_SIZE,
    skip,
  };
  if (sortBy) queryArg.sortBy = sortBy;
  if (sortDirection) queryArg.sortDirection = sortDirection;

  const { data, isFetching, isError } =
    useListBuyerEventsQuery(queryArg);

  const lastLoadedPage = useRef<typeof data>(undefined);
  if (data) lastLoadedPage.current = data;
  const page = data ?? lastLoadedPage.current;
  const events = page?.events ?? [];
  const count = page?.count ?? 0;

  const hasActiveFilters =
    !!name ||
    statusBuckets.length > 0 ||
    responsiblePersons.length > 0 ||
    contractTypes.length > 0 ||
    deadlinePassed ||
    !!sortBy ||
    skip > 0;

  const singleBucket =
    statusBuckets.length === 1 ? statusBuckets[0] : undefined;

  const statusOptions: SelectorValue[] = presentBuckets.map((bucket) => ({
    value: bucket,
    label: navLabel(BUCKET_META[bucket].labelKey),
  }));

  const columns: EntityTableColumn[] = [
    {
      key: "title",
      label: t("Title"),
      onSearch: (value: string) => debouncedSetName(value),
      currentSearchValue: name,
      hidden: !visibleColumns.has("title"),
    },
    {
      key: "internalReference",
      label: t("Internal reference"),
      onSort: handleSort(SourcingEventSortField.ReferenceNumber),
      currentSortDirection:
        sortBy === SourcingEventSortField.ReferenceNumber
          ? sortDirection
          : undefined,
      hidden: !visibleColumns.has("internalReference"),
    },
    {
      key: "status",
      label: t("Status"),
      hidden: !visibleColumns.has("status"),
      multiSelectConfig: {
        options: statusOptions,
        selectedValues: statusBuckets.map((bucket) => ({
          value: bucket,
          label: navLabel(BUCKET_META[bucket].labelKey),
        })),
        onSelect: (values) =>
          setFilterParam("status", values.map((v) => v.value).join(",")),
      },
    },
    {
      key: "responsiblePersons",
      label: t("Responsible person"),
      hidden: !visibleColumns.has("responsiblePersons"),
      multiSelectConfig: {
        options: filteredResponsibleOptions.map((p) => ({
          value: p.id,
          label: displayPersonName(p),
        })),
        selectedValues: selectedResponsibleValues,
        onSelect: (values) =>
          setFilterParam("responsible", values.map((v) => v.value).join(",")),
        onInputChange: (value) => debouncedPersonSearch(value),
      },
    },
    {
      key: "contractType",
      label: t("Contract type"),
      hidden: !visibleColumns.has("contractType"),
      multiSelectConfig: {
        options: CONTRACT_NATURES.filter((nature) =>
          presentContractNatures.has(nature),
        ).map((nature) => ({
          value: nature,
          label: getContractNatureLabel(nature, t),
        })),
        selectedValues: contractTypes.map((nature) => ({
          value: nature,
          label: getContractNatureLabel(nature, t),
        })),
        onSelect: (values) =>
          setFilterParam("contractType", values.map((v) => v.value).join(",")),
      },
    },
    {
      key: "deadline",
      label: t("Deadline"),
      onSort: handleSort(SourcingEventSortField.Deadline),
      currentSortDirection:
        sortBy === SourcingEventSortField.Deadline ? sortDirection : undefined,
      hidden: !visibleColumns.has("deadline"),
    },
    {
      key: "modifiedAt",
      label: t("Last modified"),
      onSort: handleSort(SourcingEventSortField.ModifiedAt),
      currentSortDirection:
        sortBy === SourcingEventSortField.ModifiedAt
          ? sortDirection
          : undefined,
      hidden: !visibleColumns.has("modifiedAt"),
    },
  ];

  const rows = events.map((event) => [
    <Box key="title" minW="0" maxW="80">
      <HStack spacing="2" minW="0">
        <Tooltip label={event.title || t("Untitled")}>
          <Button
            variant="link"
            color="smText"
            fontWeight="medium"
            textAlign="left"
            minW="0"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            onClick={() => navigateToEvent(event)}
          >
            {event.title || t("Untitled")}
          </Button>
        </Tooltip>
        <UnansweredQuestionsBadge count={event.unansweredQuestionsCount} />
      </HStack>
      <CopiedFromLine event={event} />
    </Box>,
    <Text key="internalReference" color="smMuted" textAlign="right">
      {event.referenceId}
    </Text>,
    <EventListItemStatusTag key="status" event={event} size="sm" />,
    <Text key="responsible" color="smMuted">
      {displayPersonName(event.responsible)}
    </Text>,
    <Text key="contractType" color="smMuted">
      {event.contractNature
        ? getContractNatureLabel(event.contractNature, t)
        : "–"}
    </Text>,
    <Text key="deadline" color="smMuted">
      {event.deadline ? displayDateWithTime(event.deadline) : "–"}
    </Text>,
    <Text key="modifiedAt" color="smMuted">
      {displayDate(event.modifiedAt)}
    </Text>,
  ]);

  return (
    <Box>
      <Heading
        as="h1"
        size="lg"
        color="smText"
        mb={deadlinePassed || singleBucket ? "6" : "2"}
      >
        {deadlinePassed
          ? t("Past their deadline")
          : singleBucket
            ? navLabel(BUCKET_META[singleBucket].labelKey)
            : navLabel("All procurements")}
      </Heading>
      {!singleBucket && !deadlinePassed && (
        <Text color="smMuted" mb="6">
          {t("All your sourcing events.")}
        </Text>
      )}

      {isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load sourcing events.")}</Text>
        </Flex>
      ) : (
        <Box
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          p="4"
        >
          <EntityTable
            emptyText={t("No sourcing events yet.")}
            noMatchText={terms.noProcurementsMatchFilters}
            columns={columns}
            rows={rows}
            size="md"
            isLoading={isFetching}
            count={count}
            limit={PAGE_SIZE}
            skip={skip}
            setSkip={(next) =>
              updateParams((params) => params.set("skip", `${next}`))
            }
            onReset={handleReset}
            hasActiveFilters={hasActiveFilters}
            resetKey={resetKey}
            onShowColumn={onShowColumn}
            onHideColumn={onHideColumn}
            hasColumnChanges={hasColumnChanges}
          />
        </Box>
      )}
    </Box>
  );
};
